// utils/queryUtils.js
import type { HTMLInputEvent } from '@/types/forms';

export function getDuplicateQueryIndices(values: Array<any>, key: string) {
  const duplicatedFacetIndices = [];
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] === key) {
      duplicatedFacetIndices.push(i);
    }
  }
  return duplicatedFacetIndices;
}

export function getQueryIndicesToRemove(values: Array<any>, key: string) {
  const indicesToRemove = [];
  const duplicateQueryIndices = getDuplicateQueryIndices(values, key);

  for (let i = 0; i < duplicateQueryIndices.length; i += 1) {
    indicesToRemove.push(duplicateQueryIndices[i]);
    indicesToRemove.push(duplicateQueryIndices[i] + 1);
  }

  return indicesToRemove;
}

export function removeIndicesFromQuery(values: Array<any>, key: string) {
  const indicesToRemove = getQueryIndicesToRemove(values, key);
  return values.filter((__, index) => !indicesToRemove.includes(index));
}

export function useFacets({
  emit,
}: {
  emit: {
    (name: 'deleteAllFacets'): void;
    (name: 'showResults'): void;
  };
}) {
  const { $i18n } = useNuxtApp();
  const route = useRoute();
  const router = useRouter();

  function parseQueryArrayFromUrl() {
    if (route.query.q) {
      return route.query.q.toString().split(':');
    }

    if (route.query.text) {
      return route.query.text.toString().split(':');
    }

    return [];
  }

  function getUpdatedRoute(oldSearch: string) {
    const path = router.resolve({ name: 'search' }).path;

    if (oldSearch && oldSearch !== ':' && oldSearch !== '::') {
      return { path, query: { q: oldSearch } };
    }

    return { path };
  }

  async function deleteAllFacets(facets: Array<any>) {
    emit('deleteAllFacets');
    emit('showResults');

    // Remove the query from current route
    if (!route.path.includes($i18n.t('url.search'))) {
      return navigateTo({
        path: route.path,
        query: {
          sort: route?.query?.sort,
        },
      });
    } else {
      let updatedQueryString = '';
      let queryArray = parseQueryArrayFromUrl();
      let isQueryArrayMutated = false;

      if (facets?.length) {
        for (let i = 0; i < facets.length; i += 1) {
          for (let j = 0; j < facets[i].values.length; j += 1) {
            if (facets[i].values[j].selected && queryArray.includes(facets[i].values[j].code)) {
              queryArray = removeIndicesFromQuery(queryArray, facets[i].code);
              isQueryArrayMutated = true;
            }
          }
        }
        updatedQueryString = queryArray.join(':');
      }

      if (!isQueryArrayMutated) {
        [updatedQueryString] = queryArray;
      }

      return navigateTo(getUpdatedRoute(updatedQueryString));
    }
  }

  const hasSearchedWithFilters = computed(() => {
    return !!route.query.q;
  });

  return {
    deleteAllFacets,
    hasSearchedWithFilters,
  };
}
